import { Draggable } from "@hello-pangea/dnd";
import { useRedirect, RecordContextProvider } from "ra-core";
import { ReferenceField } from "@/components/admin/reference-field";
import { SelectField } from "@/components/admin/select-field";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import {
  compactAmountOptions,
  formatDealAmount,
  getUserLocale,
} from "../misc/currency";

export const DealCard = ({
  deal,
  index,
  justWonId,
}: {
  deal: Deal;
  index: number;
  justWonId?: string | null;
}) => {
  if (!deal) return null;

  return (
    <Draggable draggableId={String(deal.id)} index={index}>
      {(provided, snapshot) => (
        <DealCardContent
          provided={provided}
          snapshot={snapshot}
          deal={deal}
          justWonId={justWonId}
        />
      )}
    </Draggable>
  );
};

export const DealCardContent = ({
  provided,
  snapshot,
  deal,
  justWonId,
}: {
  provided?: any;
  snapshot?: any;
  deal: Deal;
  justWonId?: string | null;
}) => {
  const isJustWon = justWonId === String(deal.id);
  const { dealCategories, currency } = useConfigurationContext();
  const amount = formatDealAmount(
    deal,
    currency,
    getUserLocale(),
    compactAmountOptions,
  );
  const redirect = useRedirect();
  const handleClick = () => {
    redirect(`/deals/${deal.id}/show`, undefined, undefined, undefined, {
      _scrollToTop: false,
    });
  };

  return (
    <div
      className="cursor-pointer"
      {...provided?.draggableProps}
      {...provided?.dragHandleProps}
      ref={provided?.innerRef}
      onClick={handleClick}
    >
      <RecordContextProvider value={deal}>
        <div
          className={`p-3 rounded-[8px] border bg-nd-surface transition-all duration-150 ${
            snapshot?.isDragging
              ? "opacity-90 border-nd-border-visible scale-[1.08] rotate-[1.5deg] shadow-xl"
              : "border-nd-border hover:border-nd-border-visible hover:-translate-y-0.5 hover:shadow-sm"
          }`}
          style={
            isJustWon ? { animation: "wonFlash 0.9s ease-out" } : undefined
          }
        >
          {/* Tertiary: company label + avatar */}
          <div className="flex items-center justify-between mb-2 gap-2 min-w-0">
            <span className="font-nd-grotesk text-[11px] text-nd-text-secondary truncate min-w-0">
              <ReferenceField
                source="company_id"
                reference="companies"
                link={false}
              />
            </span>
            <ReferenceField
              source="company_id"
              reference="companies"
              link={false}
            >
              <CompanyAvatar width={20} height={20} />
            </ReferenceField>
          </div>
          {/* Secondary: deal name */}
          <p className="font-nd-grotesk text-sm font-medium text-nd-text-primary leading-snug mb-2 line-clamp-2">
            {deal.name}
          </p>
          {/* Primary: amount in the deal's currency, with the CAD equivalent when foreign */}
          <div className="flex items-center justify-between gap-2 min-w-0">
            <span className="font-nd-mono text-base font-medium text-nd-text-display flex items-baseline gap-1.5 min-w-0">
              <span>{amount.native}</span>
              {amount.reporting && (
                <span className="text-[11px] font-normal text-nd-text-secondary truncate">
                  · {amount.reporting}
                </span>
              )}
            </span>
            {deal.category && (
              <span
                className="font-nd-grotesk text-[10px] px-1.5 py-0.5 rounded-sm"
                style={{
                  background: "var(--nd-surface-raised)",
                  color: "var(--nd-text-secondary)",
                }}
              >
                <SelectField
                  source="category"
                  choices={dealCategories}
                  optionText="label"
                  optionValue="value"
                />
              </span>
            )}
          </div>
        </div>
      </RecordContextProvider>
    </div>
  );
};
